/**
 * @file pages/well/TrenchEstimatePage.js
 * @description Well Butler — "Estimate a trench" (5th tool).
 *
 * Price a waterline or well + power run and hand the customer a quote.
 * Models the real WNCIL split: machine-trench part of the depth, then hand-dig
 * the rest. Pure field tool — all math runs on the phone, nothing hits a server.
 * The tech's customized rates + material prices persist to localStorage so the
 * shop's numbers stick between jobs.
 *
 * Formulas (locked by the handoff spec):
 *   machineLabor = length x machineRate[ground] x machineDepth x soil
 *   handLabor    = length x handRate[ground]    x handDepth    x soil
 *   runLabor     = machineLabor + handLabor
 *   materialCost = totalLength x sum(enabled cost/ft)
 *   materialSell = totalLength x sum(enabled sell/ft)
 *   subtotal     = laborTotal + materialSell + equipmentFee
 *   grandTotal   = subtotal + subtotal x markupPct/100
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Card } from '../../design-system';
import { WellPage } from './wellShared';
import { pushHistory } from '../../lib/wellHistory';

/*****************************************************************
 *  WHERE THIS SCREEN KEEPS THINGS ON THE PHONE                   *
 *  Every slot below is this device's browser storage.            *
 *****************************************************************/

const RATES_KEY = 'trenchRates_v1';                              // the shop's rates and fees
const MATS_KEY = 'trenchMaterials_v1';                           // the shop's material prices
const RATES_BEFORE_RESET_KEY = 'trenchRates_v1_beforeReset';     // rates kept just before a reset
const MATS_BEFORE_RESET_KEY = 'trenchMaterials_v1_beforeReset';  // materials kept just before a reset
const DRAFT_RUNS_KEY = 'trenchDraftRuns_v1';                     // the runs on screen, kept as a draft

// WHY (2026-10-07, James): defect #175 — "Reset to defaults" wiped the shop's
// saved rates and prices with no are-you-sure, and the estimate's runs were lost
// the moment the tech left the screen. Now: reset asks first and keeps the old
// numbers (so "Bring back my numbers" can undo it), and the runs are kept as a
// draft on this device. Nothing is thrown away.

const DEFAULT_RATES = {
  machineEven: 3.0, machineSlope: 4.5,
  handEven: 6.0, handSlope: 9.0,
  soil: 1.0, equipmentFee: 0, markupPct: 0,
};

const DEFAULT_MATERIALS = [
  { key: 'pipe', name: 'Pipe', cost: 1.25, sell: 2.75, on: true },
  { key: 'wire', name: 'Wire', cost: 1.5, sell: 3.25, on: true },
  { key: 'backfill', name: 'Backfill / cover-up', cost: 0.75, sell: 1.75, on: true },
];

const newRun = () => ({
  id: Math.random().toString(36).slice(2),
  desc: '', length: '', ground: 'even', machineDepth: '', handDepth: '',
});

const n = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

// WHY (2026-10-07, James): defect #175 proof run — a price like 2962.50 showed as
// "$2,962.5". Whole dollars still show with no cents; anything else shows both cents.
const usd = (v) => v.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
  maximumFractionDigits: 2,
});

/* ---- saveJSON(key, value) -------------------------------------
   Best-effort save to this device. Never throws.                  */
function saveJSON(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // best-effort only (private window, storage full)
  }
}

/* ---- loadDraftRuns() ------------------------------------------
   The runs the tech had on screen last time, or one blank run.    */
function loadDraftRuns() {
  const savedRuns = loadJSON(DRAFT_RUNS_KEY, null);
  if (Array.isArray(savedRuns) && savedRuns.length) {
    return savedRuns;
  }
  return [newRun()];
}

function loadJSON(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch { return fallback; }
}

export default function TrenchEstimatePage() {
  const [rates, setRates] = useState(() => ({ ...DEFAULT_RATES, ...loadJSON(RATES_KEY, {}) }));
  const [materials, setMaterials] = useState(() => {
    const saved = loadJSON(MATS_KEY, null);
    return Array.isArray(saved) && saved.length ? saved : DEFAULT_MATERIALS;
  });
  const [runs, setRuns] = useState(loadDraftRuns);
  const [showRates, setShowRates] = useState(false);
  const [isShowingMyNumbers, setIsShowingMyNumbers] = useState(false);   // cost + margin, closed on every visit
  const [isAskingToReset, setIsAskingToReset] = useState(false);         // are-you-sure for reset
  const [hasNumbersBeforeReset, setHasNumbersBeforeReset] = useState(
    () => loadJSON(RATES_BEFORE_RESET_KEY, null) !== null,
  );
  const [savedToHistoryAt, setSavedToHistoryAt] = useState(null);         // time of the last "Save to History"

  useEffect(() => {
    try { window.localStorage.setItem(RATES_KEY, JSON.stringify(rates)); } catch { /* ignore */ }
  }, [rates]);
  useEffect(() => {
    try { window.localStorage.setItem(MATS_KEY, JSON.stringify(materials)); } catch { /* ignore */ }
  }, [materials]);
  useEffect(() => {
    saveJSON(DRAFT_RUNS_KEY, runs);   // keep the estimate as a draft on this device
  }, [runs]);

  /* ---- resetToDefaults() ---------------------------------------
     The person said yes: keep the current numbers aside, then put
     the market defaults back. Never throws the old numbers away.  */
  function resetToDefaults() {
    saveJSON(RATES_BEFORE_RESET_KEY, rates);
    saveJSON(MATS_BEFORE_RESET_KEY, materials);
    setHasNumbersBeforeReset(true);
    setRates({ ...DEFAULT_RATES });
    setMaterials(DEFAULT_MATERIALS.map((m) => ({ ...m })));
    setIsAskingToReset(false);
  }

  /* ---- bringBackNumbersBeforeReset() ---------------------------
     Puts back the rates and prices kept by the last reset.        */
  function bringBackNumbersBeforeReset() {
    const savedRates = loadJSON(RATES_BEFORE_RESET_KEY, null);
    const savedMaterials = loadJSON(MATS_BEFORE_RESET_KEY, null);
    if (savedRates) {
      setRates({ ...DEFAULT_RATES, ...savedRates });
    }
    if (Array.isArray(savedMaterials) && savedMaterials.length) {
      setMaterials(savedMaterials);
    }
  }

  const setRate = (k) => (e) => setRates((r) => ({ ...r, [k]: e.target.value === '' ? '' : Number(e.target.value) }));
  const setMat = (i, field) => (e) => setMaterials((m) => m.map((row, idx) =>
    idx === i ? { ...row, [field]: field === 'on' ? e.target.checked : (e.target.value === '' ? '' : Number(e.target.value)) } : row));
  const setRun = (id, field) => (e) => setRuns((rs) => rs.map((r) => r.id === id ? { ...r, [field]: e.target.value } : r));
  const setRunGround = (id, ground) => setRuns((rs) => rs.map((r) => r.id === id ? { ...r, ground } : r));

  const calc = useMemo(() => {
    const soil = n(rates.soil) || 1;
    let machineLaborTotal = 0;
    let handLaborTotal = 0;
    let totalLength = 0;

    const perRun = runs.map((r) => {
      const len = n(r.length);
      const mRate = r.ground === 'slope' ? n(rates.machineSlope) : n(rates.machineEven);
      const hRate = r.ground === 'slope' ? n(rates.handSlope) : n(rates.handEven);
      const machineLabor = len * mRate * n(r.machineDepth) * soil;
      const handLabor = len * hRate * n(r.handDepth) * soil;
      machineLaborTotal += machineLabor;
      handLaborTotal += handLabor;
      totalLength += len;
      return { id: r.id, machineLabor, handLabor, runLabor: machineLabor + handLabor };
    });

    const laborTotal = machineLaborTotal + handLaborTotal;
    const enabled = materials.filter((m) => m.on);
    const costPerFt = enabled.reduce((s, m) => s + n(m.cost), 0);
    const sellPerFt = enabled.reduce((s, m) => s + n(m.sell), 0);
    const materialCost = totalLength * costPerFt;
    const materialSell = totalLength * sellPerFt;
    const materialMargin = materialSell - materialCost;
    const equipmentFee = n(rates.equipmentFee);
    const subtotal = laborTotal + materialSell + equipmentFee;
    const markupPct = n(rates.markupPct);
    const markup = subtotal * (markupPct / 100);
    const grandTotal = subtotal + markup;

    return {
      perRun, totalLength, machineLaborTotal, handLaborTotal, laborTotal,
      materialCost, materialSell, materialMargin, equipmentFee,
      subtotal, markupPct, markup, grandTotal,
    };
  }, [runs, rates, materials]);

  const label = { display: 'block', fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-1_5)', fontSize: 'var(--text-body-sm)' };
  const rowGap = { display: 'grid', gap: 'var(--space-3)' };

  return (
    <WellPage
      title="Estimate a trench"
      intro="Price a waterline or well + power run. Split each run into how deep the trencher goes and how deep you finish by hand — the customer price builds itself."
    >
      {/* RUNS */}
      <div style={rowGap}>
        {runs.map((r, idx) => {
          const cr = calc.perRun.find((p) => p.id === r.id) || {};
          return (
            <Card key={r.id} variant="elevated" padding="md">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                <strong>Run {idx + 1}</strong>
                {runs.length > 1 && (
                  <button type="button" onClick={() => setRuns((rs) => rs.filter((x) => x.id !== r.id))}
                    style={{ background: 'none', border: 'none', color: 'var(--text-error)', cursor: 'pointer', fontSize: 'var(--text-body-sm)' }}>
                    Remove
                  </button>
                )}
              </div>

              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label style={label}>What is this run? (optional)</label>
                <Input value={r.desc} onChange={setRun(r.id, 'desc')} placeholder="House to well" />
              </div>

              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label style={label}>Length</label>
                <Input type="number" inputMode="decimal" value={r.length} onChange={setRun(r.id, 'length')} rightAddon="ft" />
              </div>

              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label style={label}>Ground</label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  {['even', 'slope'].map((g) => (
                    <button key={g} type="button" onClick={() => setRunGround(r.id, g)}
                      style={{
                        flex: 1, padding: 'var(--space-2_5) var(--space-3)', borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--border-default)', cursor: 'pointer',
                        fontWeight: 'var(--weight-semibold)', textTransform: 'capitalize',
                        background: r.ground === g ? 'var(--color-primary)' : 'var(--surface-default)',
                        color: r.ground === g ? '#fff' : 'var(--text-primary)',
                      }}>
                      {g === 'even' ? 'Even ground' : 'Slope'}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <div style={{ flex: 1 }}>
                  <label style={label}>Machine depth</label>
                  <Input type="number" inputMode="decimal" value={r.machineDepth} onChange={setRun(r.id, 'machineDepth')} rightAddon="ft" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={label}>Hand depth</label>
                  <Input type="number" inputMode="decimal" value={r.handDepth} onChange={setRun(r.id, 'handDepth')} rightAddon="ft" />
                </div>
              </div>

              {(cr.runLabor > 0) && (
                <div style={{ marginTop: 'var(--space-3)', color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)' }}>
                  Machine {usd(cr.machineLabor)} + hand {usd(cr.handLabor)} = <strong>{usd(cr.runLabor)}</strong>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Button fullWidth variant="secondary" onClick={() => setRuns((rs) => [...rs, newRun()])} style={{ marginTop: 'var(--space-3)' }}>
        + Add another run
      </Button>

      {/* MATERIALS */}
      <Card variant="outline" padding="md" style={{ marginTop: 'var(--space-4)' }}>
        <strong>Materials</strong>
        <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', marginBottom: 'var(--space-3)' }}>
          Priced per foot over the total run length ({calc.totalLength || 0} ft). Toggle off anything the customer supplies.
        </div>
        {materials.map((m, i) => (
          <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2_5)' }}>
            <input type="checkbox" checked={!!m.on} onChange={setMat(i, 'on')} style={{ width: 20, height: 20 }} aria-label={`Include ${m.name}`} />
            <div style={{ flex: 1, minWidth: 0, fontWeight: 'var(--weight-semibold)', fontSize: 'var(--text-body-sm)', opacity: m.on ? 1 : 0.5 }}>{m.name}</div>
            <div style={{ width: 104 }}>
              <Input type="number" inputMode="decimal" value={m.cost} onChange={setMat(i, 'cost')} leftAddon="cost" />
            </div>
            <div style={{ width: 104 }}>
              <Input type="number" inputMode="decimal" value={m.sell} onChange={setMat(i, 'sell')} leftAddon="sell" />
            </div>
          </div>
        ))}
      </Card>

      {/* RATES (collapsible) */}
      <Card variant="outline" padding="md" style={{ marginTop: 'var(--space-4)' }}>
        <button type="button" onClick={() => setShowRates((s) => !s)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%', textAlign: 'left', fontWeight: 'var(--weight-bold)', fontSize: 'var(--text-body)' }}>
          {showRates ? '▾' : '▸'} Rates &amp; fees {showRates ? '' : '(tap to edit)'}
        </button>
        {showRates && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', marginBottom: 'var(--space-3)' }}>
              Dollars per foot of length, per foot of depth. Your numbers save on this device.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
              <div><label style={label}>Machine · even</label><Input type="number" inputMode="decimal" value={rates.machineEven} onChange={setRate('machineEven')} leftAddon="$" /></div>
              <div><label style={label}>Machine · slope</label><Input type="number" inputMode="decimal" value={rates.machineSlope} onChange={setRate('machineSlope')} leftAddon="$" /></div>
              <div><label style={label}>Hand · even</label><Input type="number" inputMode="decimal" value={rates.handEven} onChange={setRate('handEven')} leftAddon="$" /></div>
              <div><label style={label}>Hand · slope</label><Input type="number" inputMode="decimal" value={rates.handSlope} onChange={setRate('handSlope')} leftAddon="$" /></div>
              <div><label style={label}>Soil multiplier</label><Input type="number" inputMode="decimal" value={rates.soil} onChange={setRate('soil')} /></div>
              <div><label style={label}>Equipment fee</label><Input type="number" inputMode="decimal" value={rates.equipmentFee} onChange={setRate('equipmentFee')} leftAddon="$" /></div>
              <div><label style={label}>Markup</label><Input type="number" inputMode="decimal" value={rates.markupPct} onChange={setRate('markupPct')} rightAddon="%" /></div>
            </div>
            {!isAskingToReset && (
              <Button variant="ghost" size="sm" onClick={() => setIsAskingToReset(true)} style={{ marginTop: 'var(--space-3)', minHeight: 44 }}>
                Reset to defaults
              </Button>
            )}
            {isAskingToReset && (
              <div style={{ marginTop: 'var(--space-3)', padding: 'var(--space-3)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
                <p style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-body-sm)', lineHeight: 1.5 }}>
                  Put the market defaults back in place of your rates and material prices?
                  Your numbers are kept — "Bring back my numbers" undoes this.
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <Button variant="primary" size="sm" style={{ flex: 1, minHeight: 44 }} onClick={resetToDefaults}>Yes, use defaults</Button>
                  <Button variant="secondary" size="sm" style={{ flex: 1, minHeight: 44 }} onClick={() => setIsAskingToReset(false)}>Keep mine</Button>
                </div>
              </div>
            )}
            {hasNumbersBeforeReset && !isAskingToReset && (
              <Button variant="secondary" size="sm" onClick={bringBackNumbersBeforeReset} style={{ marginTop: 'var(--space-2)', minHeight: 44 }}>
                Bring back my numbers (from before the last reset)
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* QUOTE SUMMARY */}
      <Card variant="elevated" padding="md" style={{ marginTop: 'var(--space-5)' }}>
        <Line label="Machine trenching labor" value={usd(calc.machineLaborTotal)} />
        <Line label="Hand digging labor" value={usd(calc.handLaborTotal)} />
        <Line label="Labor total" value={usd(calc.laborTotal)} strong />
        <Line label="Materials" value={usd(calc.materialSell)} />
        {calc.equipmentFee > 0 && <Line label="Equipment / mobilization" value={usd(calc.equipmentFee)} />}
        <Line label="Subtotal" value={usd(calc.subtotal)} strong />
        {calc.markup > 0 && <Line label={`Markup (${calc.markupPct}%)`} value={usd(calc.markup)} />}

        <div style={{
          marginTop: 'var(--space-3)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)',
          background: 'var(--color-primary)', color: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontWeight: 'var(--weight-semibold)' }}>Customer price</span>
          <span style={{ fontSize: 'var(--text-heading-md, 1.5rem)', fontWeight: 'var(--weight-bold)' }}>{usd(calc.grandTotal)}</span>
        </div>

        {/* WHY (2026-10-07, James): defect #175 — cost and margin sat right under
            the customer price, so anyone shown the phone saw them. They are now
            folded away, closed every time the screen opens. */}
        <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--border-default)', fontSize: 'var(--text-body-sm)' }}>
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            style={{ minHeight: 44 }}
            aria-expanded={isShowingMyNumbers}
            onClick={() => setIsShowingMyNumbers((isOpen) => !isOpen)}
          >
            {isShowingMyNumbers ? 'Hide my numbers' : 'Show my numbers'}
          </Button>
          <div style={{ textAlign: 'center', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
            Material cost and margin. Not for the customer.
          </div>
          {isShowingMyNumbers && (
            <div style={{ marginTop: 'var(--space-2)' }}>
              <Line label="Material cost" value={usd(calc.materialCost)} muted />
              <Line label="Material margin" value={usd(calc.materialMargin)} muted />
            </div>
          )}
        </div>
      </Card>

      <Button
        fullWidth
        variant="secondary"
        style={{ marginTop: 'var(--space-3)', minHeight: 44 }}
        disabled={calc.totalLength <= 0}
        onClick={() => {
          saveEstimateToHistory(runs, calc);
          setSavedToHistoryAt(new Date());
        }}
      >
        Save this estimate to History
      </Button>
      {savedToHistoryAt && (
        <div role="status" style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>
          ✅ Saved to History on this device at {savedToHistoryAt.toLocaleTimeString()}.
        </div>
      )}
      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        Your runs stay on this screen as a draft on this phone, even if you leave and come back.
      </div>

      <p style={{ marginTop: 'var(--space-5)', color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', lineHeight: 1.5 }}>
        Rates are market-seeded defaults — edit them to your shop under “Rates &amp; fees.” Always eyeball the total against the job before you hand it over.
      </p>
    </WellPage>
  );
}

/* ---- saveEstimateToHistory(runs, calc) ---------------------------
   Writes one line to this device's Well History: what the runs
   were, the total feet, and the customer price. Never the cost or
   margin (History can be shown to a customer too).               */
function saveEstimateToHistory(runs, calc) {
  const namedRuns = runs.map((run) => run.desc).filter((description) => description && description.trim());
  const runWord = runs.length === 1 ? 'run' : 'runs';
  const titleWords = namedRuns.length ? namedRuns.join(', ') : `${runs.length} ${runWord}`;
  pushHistory({
    kind: 'trench',
    title: `Trench estimate — ${titleWords}`,
    summary: `${calc.totalLength} ft · customer price ${usd(calc.grandTotal)}`,
  });
}

function Line({ label, value, strong, muted }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: 'var(--space-1_5) 0',
      color: muted ? 'var(--text-tertiary)' : 'var(--text-primary)',
      fontWeight: strong ? 'var(--weight-bold)' : 'var(--weight-regular)',
      fontSize: muted ? 'var(--text-body-sm)' : 'var(--text-body)',
    }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
